import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { AnimatePresence, motion } from 'motion/react';
import { CloseIcon, SearchIcon } from '@wbk/svg';
import { useRouter } from '@wbk/config';
import { Button, ImageWithFallback, ImageWithPlaceholder, Input, Typography } from '@wbk/ui';
import { useScrollLock } from '@/hooks/use-scroll-lock';
import useLocalization from '@/context/localization/useLocalization';
import { sanitizeInput } from '@/utils';

type Props = {
  open: boolean;
  onOpenChange: () => void;
};

export default function GlobalSearch({ open, onOpenChange }: Props) {
  const [searchParams] = useSearchParams();
  const search = searchParams.get('q') || '';
  const { t } = useTranslation('common');

  const router = useRouter();

  const { locale } = useLocalization();
  const [searchTerm, setSearchTerm] = useState(search);

  const { lock, unlock } = useScrollLock({
    autoLock: false,
  });

  useEffect(() => {
    if (open) {
      lock();
    } else {
      unlock();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps -- we only want to run this when isOpen changes, adding
  }, [open]); // lock and toggleShowTimer will cause unwanted behavior

  useEffect(() => {
    setSearchTerm(search);
  }, [search]);

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: 'easeInOut' }}
            className='pointer-events-auto fixed inset-0 z-[999]'
          >
            <div className='h-full w-full bg-black/95'>
              <div className='container relative z-20 flex w-full justify-between gap-6 px-4 py-8 md:px-8 2xl:gap-10'>
                <Link to={`/${locale}`}>
                  <ImageWithPlaceholder
                    src='/images/logo.svg'
                    alt='Logo'
                    className='w-20 xl:w-28'
                  />
                </Link>

                <div className='px-4'>
                  <Button
                    rounded
                    onClick={() => {
                      onOpenChange?.();
                    }}
                    theme='transparent'
                    className='border-primary hover:bg-primary h-9 w-9 rounded-full border bg-transparent p-0 transition-all duration-200'
                  >
                    <CloseIcon />
                  </Button>
                </div>
              </div>

              <div className='absolute inset-0 z-10 flex h-full flex-col items-center justify-center gap-10 px-4'>
                <div className='flex flex-col items-center justify-center md:gap-6'>
                  <ImageWithFallback src='/icons/common/search.svg' alt='Search' />

                  <div className='mt-4 flex flex-col md:mt-0 md:gap-6 rtl:md:gap-10'>
                    <Typography
                      as='h3'
                      variant='heading-L'
                      strong
                      className='2xl:text-display-S font-gellix rtl:font-expo-arabic text-center font-extrabold uppercase md:text-[50px] md:leading-[48px] ltr:tracking-tighter'
                      style={{
                        willChange: 'transform',
                      }}
                    >
                      {t('search_for')}
                    </Typography>
                    <Typography
                      as='h3'
                      strong
                      variant='heading-L'
                      className='2xl:text-display-S font-gellix rtl:font-expo-arabic text-center font-extrabold uppercase md:text-[50px] md:leading-[48px] ltr:tracking-tighter'
                      style={{
                        willChange: 'transform',
                      }}
                    >
                      {t('whatever_you_want')}
                    </Typography>
                  </div>
                </div>

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    router.push(`/${locale}/explore?q=${encodeURIComponent(searchTerm)}`);
                    onOpenChange?.();
                    setSearchTerm('');
                  }}
                  className='relative mx-auto w-full max-w-xl'
                >
                  <Input
                    value={searchTerm}
                    type='text'
                    autoFocus
                    onChange={(v) => {
                      const text = sanitizeInput(v);
                      setSearchTerm(text);
                    }}
                    placeholder={t('global_search_placeholder')}
                    className='h-16 w-full rounded-full bg-white/20 px-4 text-base placeholder:text-white/50 hover:bg-white/20 focus-visible:bg-white/20 ltr:pr-[18%] ltr:md:pr-16 rtl:pl-[18%] rtl:md:pl-16'
                  />
                  <div className='absolute top-1/2 -translate-y-1/2 ltr:right-2 rtl:left-2'>
                    <Button type='submit' className='size-12 rounded-full p-0'>
                      <SearchIcon className='w-5' />
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

type SearchButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement>;

export function SearchButton({ className, ...props }: SearchButtonProps) {
  return (
    <Button
      rounded
      theme='transparent'
      className={twMerge(
        'border-primary hover:bg-primary h-9 w-9 rounded-full border bg-transparent p-0 transition-all duration-200',
        className
      )}
      {...props}
    >
      <ImageWithFallback src='/icons/common/search.svg' alt='Search' className='w-5' />
    </Button>
  );
}
